import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import { ListItem, ListItemText, ListItemIcon } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export default function MultiSelectAutocomplete({
  label,
  options = [],
  value = [],
  onChange,
  multiple = true,
  placeholder = 'Selecione...',
  size = 'small',
  disabled = false,
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const allSelected = options.length > 0 && value.length === options.length;
  const someSelected = value.length > 0 && value.length < options.length;

  const handleToggleSelectAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange([...options]);
    }
  };

  const optionsWithSelectAll = options.length > 0
    ? [{ __selectAll: true, label: allSelected ? 'Desmarcar todos' : 'Selecionar todos' }, ...options]
    : [];

  const getOptionLabel = (option) => {
    if (option?.__selectAll) return option.label;
    return typeof option === 'string' ? option : String(option);
  };

  const isOptionEqualToValue = (option, val) => {
    if (option?.__selectAll) return false;
    // Normaliza strings para comparação (trim e case-insensitive)
    const optStr = String(option).trim();
    const valStr = String(val).trim();
    return optStr === valStr;
  };

  return (
    <Autocomplete
      multiple={multiple}
      options={optionsWithSelectAll}
      value={value}
      onChange={(event, newValue, reason, details) => {
        if (details?.option?.__selectAll) {
          handleToggleSelectAll();
          return;
        }
        const filtered = newValue.filter((opt) => !opt?.__selectAll);
        onChange(filtered);
      }}
      disableCloseOnSelect={multiple}
      ListboxProps={{ style: { maxHeight: 280 } }}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      disabled={disabled}
      sx={{
        '& .MuiAutocomplete-inputRoot': {
          flexWrap: 'nowrap',
          overflowX: 'auto',
          paddingRight: '60px',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { height: '6px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: isDarkMode ? '#555' : '#c4c4c4',
            borderRadius: '3px',
          },
        },
        '& .MuiAutocomplete-tag': {
          flexShrink: 0,
          margin: '2px 4px 2px 0',
        },
      }}
      renderTags={(selected, getTagProps) =>
        selected.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={`${option}-${index}`}
            label={option}
            size="small"
            sx={{
              bgcolor: '#6107F3',
              color: '#fff',
              '& .MuiChip-deleteIcon': { color: '#fff' },
              '& .MuiChip-deleteIcon:hover': { color: '#E6CCFF' },
              flexShrink: 0,
              height: '28px',
            }}
          />
        ))
      }
      renderOption={(props, option, { selected }) => {
        const isSelectAll = option?.__selectAll;

        if (isSelectAll) {
          return (
            <ListItem
              key="select-all"
              dense
              component="div"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();  // Adicionado para prevenir seleção padrão
                handleToggleSelectAll();
              }}
              sx={{
                fontWeight: 600,
                borderBottom: '1px solid #e0e0e0',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#F3E5FF' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Checkbox
                  edge="start"
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  color="primary"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleToggleSelectAll();
                  }}
                />
              </ListItemIcon>
              <ListItemText primary={option.label} />
            </ListItem>
          );
        }

        // Para opções normais: usar selected do Autocomplete
        return (
          <ListItem
            {...props}
            key={option}  // Use a opção real como key
            dense
            sx={{
              color: selected ? '#6107F3' : undefined,
              fontWeight: selected ? 600 : undefined,
              backgroundColor: selected ? '#F3E5FF' : undefined,
              '&:hover': { backgroundColor: '#F3E5FF' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Checkbox
                edge="start"
                checked={selected}
                color="primary"
                size="small"
              />
            </ListItemIcon>
            <ListItemText primary={option} />
          </ListItem>
        );
      }}
      renderInput={(params) => {
        const customPlaceholder = (
          <span
            style={{
              color: isDarkMode
                ? (disabled ? '#cccccc' : '#ffffff')
                : '#999999',
              opacity: 1,
            }}
          >
            {value.length === 0 ? placeholder : ''}
          </span>
        );
        return (
          <TextField
            {...params}
            label={label}
            placeholder=""
            size={size}
            disabled={disabled}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              style: { overflow: 'hidden' },
              placeholder: null,
              startAdornment: (
                <>
                  {params.InputProps.startAdornment}
                  {value.length === 0 && customPlaceholder}
                </>
              ),
            }}
          />
        );
      }}
    />
  );
}